import EngagementIcon from '@assets/icons/EngagementIcon'
import GiftIcon from '@assets/icons/GiftIcon'
import IncentiveIcon from '@assets/icons/IncentiveIcon'
import IntegrationsIcon from '@assets/icons/IntegrationsIcon'
import PlatformSetupIcon from '@assets/icons/PlatformSetupIcon'
import ToolIcon from '@assets/icons/ToolIcon'
import { hasFeatureFlag } from '@globals/featureFlagUtils'
import AppPaths from '@globals/paths'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import Diversity1OutlinedIcon from '@mui/icons-material/Diversity1Outlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import Stack from '@mui/material/Stack'
import { AppPermission } from '@shared/permissions'
import NewSpaceNavChip from '@tenant/spaces/NewSpaceNavChip'
import { T } from '@transifex/react'
import { NavHiddenItem, NavItem } from './types'

export const userNavConfig: NavItem[] = [
  {
    type: 'link',
    name: <T _str="My tenants" />,
    path: AppPaths.user.tenants(),
    icon: <DashboardOutlinedIcon />,
    key: 'tenants',
  },
]

export const tenantNavHiddenConfig: NavHiddenItem[] = [
  {
    path: AppPaths.tenant.viewCard(':cardId'),
    key: 'viewCard',
    type: 'hiddenNav',
    pathMatch: [/^\/card\//],
  },
  {
    path: AppPaths.tenant.viewCardContributors(':cardId'),
    key: 'viewCardContributors',
    type: 'hiddenNav',
    pathMatch: [/^\/card\//],
  },
]

export const tenantNavConfig: NavItem[] = [
  {
    type: 'link',
    name: <T _str="Home" _comment="Translated" />,
    path: AppPaths.tenant.home(),
    icon: <HomeOutlinedIcon />,
    hideOnMobile: true,
    key: 'home',
    pathMatch: [/^\/check-ins\/category/, /^\/announcements\/[^]*/, /^\/recognition\/send\/[^]*/],
  },
  {
    type: 'link',
    name: <T _str="Celebrations" />,
    path: AppPaths.tenant.celebrate(),
    icon: <CelebrationOutlinedIcon />,
    key: 'celebrate',
    pathMatch: [/^\/celebrate\//],
    visible: hasAppPermission =>
      hasAppPermission('celebrationApp', AppPermission.CelebrationAppBrowse) ||
      hasAppPermission('cardApp', AppPermission.CardAppBrowse),
  },
  {
    type: 'link',
    name: <T _str="Awards" />,
    path: AppPaths.tenant.awards(),
    icon: <EmojiEventsOutlinedIcon />,
    key: 'awards',
    pathMatch: [/^\/awards$/],
    visible: hasAppPermission => hasAppPermission('awardApp', AppPermission.AwardAppBrowse),
  },
  {
    type: 'link',
    name: <T _str="Incentives" />,
    path: AppPaths.tenant.incentives(),
    icon: <IncentiveIcon fill="inherit" />,
    key: 'incentives',
    pathMatch: [/^\/incentives/],
    visible: hasAppPermission => hasAppPermission('incentiveApp', AppPermission.IncentiveAppBrowse),
    showUpgradeDialog: hasAppPermission =>
      hasAppPermission('incentiveApp', AppPermission.IncentiveAppUpgradeLicense),
  },
  {
    type: 'link',
    name: <T _str="Rewards" />,
    path: AppPaths.tenant.rewardsProviders(),
    icon: <GiftIcon />,
    key: 'rewards',
    pathMatch: [
      /^\/rewards\/providers/,
      /^\/rewards\/redemption-history/,
      /^\/rewards\/(\/|$)/,
      /^\/rewards\/[^/]+\/[^/]+/,
    ],
    visible: hasAppPermission =>
      hasAppPermission('rewardsApp', AppPermission.RewardsAppRedeem) &&
      hasAppPermission('pointsApp', AppPermission.PointsAppRedeemPoints),
  },
  {
    type: 'link',
    name: <T _str="People" />,
    path: AppPaths.tenant.people(),
    icon: <GroupsOutlinedIcon />,
    key: 'userDirectory',
    pathMatch: [/^\/user-directory$/],
  },
  {
    type: 'link',
    name: <T _str="Culture" />,
    path: AppPaths.tenant.culture(),
    icon: <Diversity1OutlinedIcon />,
    key: 'culture',
    pathMatch: [/^\/culture$/],
    visible: hasAppPermission => hasAppPermission('valueApp', AppPermission.ValueAppBrowseCulture),
    showUpgradeDialog: hasAppPermission =>
      hasAppPermission('recognitionApp', AppPermission.RecognitionAppUpgradeLicense),
  },
  {
    type: 'link',
    icon: <GroupWorkOutlinedIcon />,
    name: (
      <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap gap={1}>
        <span>
          <T _str="Spaces" />
        </span>
        <NewSpaceNavChip />
      </Stack>
    ),
    path: AppPaths.tenant.spaces(),
    key: 'spaces',
    visible: hasAppPermission =>
      hasAppPermission('spacesApp', AppPermission.SpacesAppBrowse) && hasFeatureFlag('spacesFlag'),
    pathMatch: [/^\/spaces\//],
  },
  {
    type: 'link',
    icon: <FolderOutlinedIcon />,
    name: <T _str="Resource hub" />,
    path: AppPaths.tenant.resourceHub('recommended'),
    key: 'resourceHub',
    visible: hasAppPermission => hasAppPermission('resourceApp', AppPermission.ResourceAppBrowse),
    showUpgradeDialog: hasAppPermission =>
      hasAppPermission('resourceApp', AppPermission.ResourceAppUpgradeLicense),
    pathMatch: [/^\/resource-hub\//],
  },
]

export const adminNavConfig: NavItem[] = [
  {
    type: 'link',
    name: <T _str="Dashboard" />,
    path: AppPaths.tenant.admin.dashboard(),
    icon: <TableChartOutlinedIcon />,
    key: 'dashboard',
    hideOnMobile: true,
    visible: hasAppPermission =>
      hasAppPermission('onboardingApp', AppPermission.OnboardingAppManage),
    pathMatch: [/^\/dashboard/],
  },
  {
    type: 'link',
    name: <T _str="Integrations" />,
    path: AppPaths.tenant.admin.integrations(),
    icon: <IntegrationsIcon />,
    hideOnMobile: true,
    key: 'integrations',
    visible: hasAppPermission =>
      hasAppPermission('tvApp', AppPermission.TvAppBrowse) ||
      hasAppPermission('identityApp', AppPermission.IdentityAppBrowse) ||
      hasAppPermission('hrIntegrationApp', AppPermission.HrIntegrationAppBrowse),
    pathMatch: [/^\/integrations(\/|$)/],
  },
  {
    type: 'group',
    icon: <GroupOutlinedIcon />,
    name: <T _str="User management" />,
    key: 'userManagement',
    children: [
      {
        type: 'link',
        name: <T _str="HRIS integration" />,
        path: AppPaths.tenant.admin.hrisIntegrations('records'),
        key: 'hrisIntegration',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
        pathMatch: [
          /^\/staffing\/import\/integrations/,
          /^\/staffing\/batch-add(\/|$)/,
          /^\/staffing(\/|$)/,
        ],
      },
      {
        type: 'link',
        name: <T _str="Users & teams" />,
        path: AppPaths.tenant.admin.members('users'),
        key: 'usersAndTeams',
        mobileIcon: <GroupOutlinedIcon />,
        pathMatch: [
          // Note, this can be done in one RegEx, but we
          // split it apart for readability
          /^\/manage\/(teams|users|quick-teams)/,
          /^\/team(\/|$)/,
          /^\/user(\/|$)/,
        ],
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
      },
      {
        type: 'link',
        name: <T _str="Security & access" />,
        path: AppPaths.tenant.admin.security(),
        key: 'securityAndAccess',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
        pathMatch: [/^\/tenant\/security/],
      },
      {
        type: 'link',
        name: <T _str="Delegation" />,
        path: AppPaths.tenant.admin.delegation(),
        hideOnMobile: true,
        key: 'delegation',
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppManageDelegation) ||
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowseDelegators),
        pathMatch: [/^\/delegation(\/|$)/, /^\/delegation\/[^/]+\/edit/],
      },
    ],
  },
  {
    type: 'group',
    icon: <PlatformSetupIcon />,
    name: <T _str="Platform set up" />,
    key: 'platformSetup',
    children: [
      {
        type: 'link',
        name: <T _str="Branding" />,
        path: AppPaths.tenant.admin.customize(),
        key: 'branding',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
        pathMatch: [/^\/customize/],
      },
      {
        type: 'link',
        name: <T _str="Recognition" />,
        path: AppPaths.tenant.admin.manageRecognition(),
        key: 'recognition',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('recognitionApp', AppPermission.RecognitionAppManage) &&
          hasAppPermission('pointsApp', AppPermission.PointsAppViewPointsConfiguration),
        pathMatch: [/^\/recognition\/manage/],
      },
      {
        type: 'link',
        name: <T _str="Values & qualities" />,
        path: AppPaths.tenant.admin.values(),
        key: 'valuesAndQualities',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('valueApp', AppPermission.ValueAppCreateValue),
        pathMatch: [/^\/values(\/|$)/],
      },
      {
        type: 'link',
        name: <T _str="Language & time" />,
        path: AppPaths.tenant.admin.localization(),
        key: 'languageAndTime',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
        pathMatch: [/^\/localization/],
      },
      {
        type: 'link',
        name: <T _str="Notifications" />,
        path: AppPaths.tenant.admin.notifications(),
        key: 'notifications',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission(
            'recognitionApp',
            AppPermission.RecognitionAppManageNotificationSettings,
          ),
        pathMatch: [/^\/notifications/],
      },
    ],
  },
  {
    type: 'group',
    icon: <GiftIcon />,
    name: <T _str="Points & rewards" />,
    key: 'pointsAndRewards',
    children: [
      {
        type: 'link',
        name: <T _str="Points" />,
        path: AppPaths.tenant.admin.points('allocation'),
        key: 'points',
        hideOnMobile: true,
        visible: hasAppPermission => hasAppPermission('pointsApp', AppPermission.PointsAppBrowse),
        pathMatch: [/^\/points(\/|$)/],
      },
      {
        type: 'link',
        name: <T _str="Rewards" />,
        path: AppPaths.tenant.admin.rewards('setup'),
        key: 'rewards',
        hideOnMobile: true,
        pathMatch: [
          /^\/rewards\/setup/,
          /^\/rewards\/reporting/,
          /^\/rewards\/([^/]+)\/edit/,
          /^\/rewards\/configure(\/|$)/,
          /^\/rewards\/store(\/|$)/,
          /^\/rewards\/store\/[^/]+\/add-funds/,
        ],
        visible: hasAppPermission =>
          hasAppPermission('rewardsApp', AppPermission.RewardsAppAdmin) &&
          (hasAppPermission('rewardsApp', AppPermission.RewardsAppViewRedemptionHistory) ||
            hasAppPermission('pointsApp', AppPermission.PointsAppViewTransactions)),
      },
    ],
  },
  {
    type: 'group',
    icon: <EngagementIcon />,
    name: <T _str="Engagement" />,
    key: 'engagement',
    children: [
      {
        type: 'link',
        name: <T _str="Awards & nominations" />,
        path: AppPaths.tenant.admin.awards('awards'),
        key: 'awards',
        mobileIcon: <EmojiEventsOutlinedIcon />,
        visible: hasAppPermission =>
          hasAppPermission('awardApp', AppPermission.AwardAppManage) ||
          hasAppPermission('awardApp', AppPermission.AwardAppNominationSender),
        pathMatch: [
          /^\/awards\/configure/,
          /^\/awards\/list/,
          /^\/awards\/award/,
          /^\/awards\/nomination\/[^/]+/,
        ],
      },
      {
        type: 'link',
        name: <T _str="Incentives" />,
        path: AppPaths.tenant.admin.manageIncentives(),
        key: 'incentives',
        mobileIcon: <IncentiveIcon fill="inherit" />,
        visible: hasAppPermission =>
          hasAppPermission('incentiveApp', AppPermission.IncentiveAppReview),
        showUpgradeDialog: hasAppPermission =>
          hasAppPermission('incentiveApp', AppPermission.IncentiveAppUpgradeLicense),
        pathMatch: [
          /^\/incentives\/configure/,
          /^\/incentives\/manage/,
          /^\/incentives\/manage\/[^/]+/,
          /^\/incentives\/duplicate\/[^/]+/,
        ],
      },
      {
        type: 'link',
        name: <T _str="Announcements" />,
        path: AppPaths.tenant.admin.announcements(),
        key: 'announcements',
        mobileIcon: <CampaignOutlinedIcon />,
        visible: hasAppPermission =>
          hasAppPermission('announcementApp', AppPermission.AnnouncementAppManage),
        pathMatch: [/^\/announcements\/configure/, /^\/announcements/],
      },
      {
        type: 'link',
        name: <T _str="Spaces" />,
        path: AppPaths.tenant.admin.spacesManage('overview'),
        key: 'spaces',
        hideOnMobile: true,
        visible: hasAppPermission => hasAppPermission('spacesApp', AppPermission.SpacesAppManage),
        showUpgradeDialog: hasAppPermission =>
          hasAppPermission('spacesApp', AppPermission.SpacesAppUpgradeLicense),
        pathMatch: [/^\/spaces\/manage(\/overview|\/permission)?$/],
      },
      {
        type: 'link',
        name: <T _str="Check-ins" />,
        path: AppPaths.tenant.admin.checkInManage(),
        key: 'checkIns',
        hideOnMobile: true,
        visible: hasAppPermission => hasAppPermission('checkInApp', AppPermission.CheckInAppManage),
        showUpgradeDialog: hasAppPermission =>
          hasAppPermission('checkInApp', AppPermission.CheckInAppUpgradeLicense),
        pathMatch: [/^\/check-ins\/manage$/, /^\/check-ins\/category\/[^/]+\/audience$/],
      },
      {
        type: 'link',
        name: <T _str="Celebrations" />,
        path: AppPaths.tenant.admin.configureCelebrations('privacy'),
        key: 'celebrations',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('celebrationApp', AppPermission.CelebrationAppEditTenantPreferences),
        pathMatch: [/^\/celebrations\/configure/],
      },
    ],
  },
  {
    type: 'group',
    icon: <ToolIcon />,
    name: <T _str="Admin tools" />,
    key: 'adminTools',
    children: [
      {
        type: 'link',
        name: <T _str="Flagged content" />,
        path: AppPaths.tenant.admin.flaggedContent('awards'),
        key: 'flaggedContent',
        mobileIcon: <FlagOutlinedIcon />,
        visible: hasAppPermission =>
          hasAppPermission('recognitionApp', AppPermission.RecognitionAppModerate) ||
          hasAppPermission('commentApp', AppPermission.CommentAppModerate),
        pathMatch: [/^\/content\/flagged\//],
      },
      {
        type: 'link',
        name: <T _str="Burnout insights" />,
        path: AppPaths.tenant.admin.analytics('insights'),
        key: 'burnoutInsights',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('checkInApp', AppPermission.CheckInAppViewAnalytics) ||
          hasAppPermission('metricApp', AppPermission.MetricAppBrowse),
        pathMatch: [/^\/analytics\//],
      },
    ],
  },
  {
    type: 'link',
    name: <T _str="Reports" />,
    path: AppPaths.tenant.admin.allReports(),
    key: 'allReports',
    icon: <SummarizeOutlinedIcon />,
    visible: hasAppPermission => hasAppPermission('reportingApp', AppPermission.ReportingAppBrowse),
    pathMatch: [/^\/reports\//, /^\/points\/reporting\/[^/]+/],
  },
  {
    type: 'link',
    name: <T _str="Logs" />,
    path: AppPaths.tenant.admin.logs(),
    hideOnMobile: true,
    icon: <ContentPasteOutlinedIcon />,
    key: 'logs',
    visible: () => false, // TODO: RE: SYN-1296 - hide nav but make route accessible
    // visible: hasAppPermission => hasAppPermission('auditingApp', AppPermission.AuditingAppBrowse),
  },
]
